import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import type { User } from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/api/firebase'
import { isFullAdminProfile, canManageStaticPages as profileCanManageSites, adminNavigation, canAccessAdminPath, adminHomePath } from '@/utils/adminAccess'

export interface UserProfile {
  status?: string
  permissions?: string[]
  uid: string
  email: string
  role: 'admin' | 'affiliate' | 'customer' | 'tenant' | null
  adminRole?: 'super_admin' | 'admin' | 'staff' | 'sales' | 'accounting' | 'op' | null
  displayName?: string
  phone?: string
  isFirstLogin?: boolean
  createdAt?: any
  updatedAt?: any
  activeTenantId?: string | null
  tenantRole?: 'owner' | 'admin' | 'agent' | 'viewer' | null
}

export interface TenantProfile {
  id: string
  slug: string
  legalName: string
  brandName: string
  registrationNumber: string
  licenseClass: 'comprehensive' | 'class_a' | 'class_b'
  status: 'active' | 'suspended'
  branding: {
    tagline?: string | null
    primaryColor: string
    logoUrl?: string | null
    websiteUrl?: string | null
    privacyPolicyUrl: string
  }
  publicContact: {
    phone: string
    email?: string | null
    address: string
  }
  verification?: {
    status: 'verified'
    verifiedAt: any
    expiresAt: any
  }
}

export interface AffiliateBankInfo {
  bankCode: string
  bankName: string
  branchName: string
  accountName: string
  accountNumber: string
  updatedAt?: any
  updatedBy?: string
}

export interface AffiliateMetrics {
  period: string
  orderCount: number
  confirmedCount: number
  completedCount: number
  cancelledCount: number
  totalHeadcount: number
  totalBonus: number
  totalDownlineBonus: number
  totalEarnings: number
  downlineStats: {
    activeCount: number
    totalCommission: number
  } | null
  updatedAt?: any
}

export interface AffiliateProfile {
  displayName: string
  phone: string
  email: string
  address: string
  status: 'active' | 'suspended'
  refCode: string
  parentId: string | null
  parentRefCode: string | null
  depth: number
  directDownlineCount: number
  isFirstLogin?: boolean
  suspendedAt?: any
  suspendedBy?: string | null
  suspendReason?: string | null
  lastLoginAt?: any
  createdAt?: any
  updatedAt?: any
  approvedBy?: string | null
  onboarding?: {
    applicationId: string | null
    source: string | null
    lineDisplayName: string | null
    lineId: string | null
    lineJoinedAt: any
    firstLoginAt: any
    firstShareAt: any
    payoutProfileStatus: 'not_required' | 'required' | 'complete'
  }
  // Bank info and metrics are now in subcollections
  bankInfo?: AffiliateBankInfo | null
  currentMetrics?: AffiliateMetrics | null
}

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<User | null>(null)
  const userProfile = ref<UserProfile | null>(null)
  const affiliateProfile = ref<AffiliateProfile | null>(null)
  const tenantProfile = ref<TenantProfile | null>(null)
  const loading = ref(true)
  let profileLoadGeneration = 0

  const recordFirstAffiliateLogin = async (uid: string) => {
    const storageKey = `affiliate_first_login_tracked:${uid}`
    if (localStorage.getItem(storageKey) === 'true') return
    const sessionId = localStorage.getItem('affiliate_funnel_session_id')
      || (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `session_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`)
    localStorage.setItem('affiliate_funnel_session_id', sessionId)
    try {
      const trackFunnelEvent = httpsCallable(functions, 'trackFunnelEvent')
      await trackFunnelEvent({ eventType: 'first_login', sessionId, channel: 'direct' })
      localStorage.setItem(storageKey, 'true')
    } catch (error) {
      console.warn('Could not record affiliate onboarding milestone:', error)
    }
  }

  // Computed properties
  const isLoggedIn = computed(() => !!currentUser.value)
  const isAdmin = computed(() => userProfile.value?.role === 'admin')
  const isAffiliate = computed(() => userProfile.value?.role === 'affiliate')
  const isCustomer = computed(() => userProfile.value?.role === 'customer')
  const isTenantMember = computed(() => Boolean(
    tenantProfile.value
      && userProfile.value?.activeTenantId
      && userProfile.value?.tenantRole
  ))
  const canManageTenant = computed(() => ['owner', 'admin'].includes(userProfile.value?.tenantRole || ''))
  const canOperateTenant = computed(() => ['owner', 'admin', 'agent'].includes(userProfile.value?.tenantRole || ''))
  const isSales = computed(() => userProfile.value?.role === 'admin' && userProfile.value?.adminRole === 'sales')
  const isAccounting = computed(() => userProfile.value?.role === 'admin' && userProfile.value?.adminRole === 'accounting')
  const isOP = computed(() => userProfile.value?.role === 'admin' && userProfile.value?.adminRole === 'op')
  const isFullAdmin = computed(() => isFullAdminProfile(userProfile.value))
  const canManageStaticPages = computed(() => profileCanManageSites(userProfile.value))
  const adminMenu = computed(() => adminNavigation(userProfile.value))
  const adminHome = computed(() => adminHomePath(userProfile.value))
  const canAccessAdmin = (path: string) => canAccessAdminPath(userProfile.value, path)
  const isApprovedAffiliate = computed(() => {
    const status = affiliateProfile.value?.status
    return isAffiliate.value && status === 'active'
  })

  // Initialize auth state listener
  const initAuth = () => {
    return new Promise<void>((resolve) => {
      // If already initialized and not loading, just resolve
      if (!loading.value && currentUser.value !== undefined) {
        resolve()
        return
      }

      onAuthStateChanged(auth, async (user) => {
        currentUser.value = user
        
        if (user) {
          try {
            await loadUserProfile(user.uid)
            if (userProfile.value?.role === 'affiliate') {
              await loadAffiliateProfile(user.uid)
              await recordFirstAffiliateLogin(user.uid)
            }
          } catch (error) {
            console.error('Error loading profiles:', error)
          }
        } else {
          userProfile.value = null
          affiliateProfile.value = null
          tenantProfile.value = null
        }
        
        loading.value = false
        resolve()
        // Keep listening to auth changes (don't unsubscribe)
      })
    })
  }

  // Load user profile from Firestore
  // admins collection stores system admins
  // affiliates collection stores affiliate data
  const loadUserProfile = async (uid: string) => {
    const generation = ++profileLoadGeneration
    try {
      // First, check Firebase Auth custom claims
      const idTokenResult = await currentUser.value?.getIdTokenResult(true) // Force refresh
      if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
      const authRole = idTokenResult?.claims?.role as 'admin' | 'affiliate' | 'customer' | 'tenant' | null
      const activeTenantId = typeof idTokenResult?.claims?.activeTenantId === 'string'
        ? idTokenResult.claims.activeTenantId
        : null
      const claimedTenantRole = ['owner', 'admin', 'agent', 'viewer'].includes(String(idTokenResult?.claims?.tenantRole))
        ? idTokenResult?.claims?.tenantRole as 'owner' | 'admin' | 'agent' | 'viewer'
        : null

      tenantProfile.value = null
      let tenantMemberData: Record<string, any> | null = null
      let tenantRole: 'owner' | 'admin' | 'agent' | 'viewer' | null = null
      if (activeTenantId && claimedTenantRole) {
        const [tenantDoc, memberDoc] = await Promise.all([
          getDoc(doc(db, 'tenants', activeTenantId)),
          getDoc(doc(db, 'tenants', activeTenantId, 'members', uid)),
        ])
        const memberData = memberDoc.exists() ? memberDoc.data() : null
        if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
        const sourceRole = memberData?.role
        if (
          tenantDoc.exists()
          && tenantDoc.data().status === 'active'
          && memberData?.status === 'active'
          && ['owner', 'admin', 'agent', 'viewer'].includes(sourceRole)
        ) {
          tenantProfile.value = { id: activeTenantId, ...tenantDoc.data() } as TenantProfile
          tenantMemberData = memberData
          // Membership is authoritative; claims only select the active tenant.
          tenantRole = sourceRole as 'owner' | 'admin' | 'agent' | 'viewer'
        }
      }

      // Read from appropriate collection based on role
      if (authRole === 'admin') {
        // Admin reads from admins collection
        try {
          const adminDoc = await getDoc(doc(db, 'admins', uid))
          if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
          if (adminDoc.exists()) {
            const firestoreData = adminDoc.data()
            userProfile.value = { 
              uid, 
              ...firestoreData,
              role: authRole,
              adminRole: firestoreData.role,
              activeTenantId,
              tenantRole,
            } as UserProfile
          } else {
            // If admins document doesn't exist, create basic profile from auth data
            userProfile.value = {
              uid,
              email: currentUser.value?.email || '',
              role: authRole,
              displayName: currentUser.value?.displayName || undefined,
              activeTenantId,
              tenantRole,
            } as UserProfile
          }
        } catch (firestoreError) {
          if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
          // If Firestore read fails, still set the profile from auth data
          console.warn('Firestore read failed, using auth data:', firestoreError)
          userProfile.value = {
            uid,
            email: currentUser.value?.email || '',
            role: authRole,
            displayName: currentUser.value?.displayName || undefined,
            activeTenantId,
            tenantRole,
          } as UserProfile
        }
      } else if (authRole === 'affiliate') {
        // Affiliate reads from affiliates collection
        const affiliateDoc = await getDoc(doc(db, 'affiliates', uid))
        if (affiliateDoc.exists()) {
          const affiliateData = affiliateDoc.data()
          userProfile.value = { 
            uid, 
            email: affiliateData.email || currentUser.value?.email || '',
            role: authRole,
            displayName: affiliateData.displayName,
            phone: affiliateData.phone,
            isFirstLogin: affiliateData.isFirstLogin,
            createdAt: affiliateData.createdAt,
            updatedAt: affiliateData.updatedAt,
            activeTenantId,
            tenantRole,
          } as UserProfile
        } else {
          // If affiliates document doesn't exist, set basic data
          userProfile.value = {
            uid,
            email: currentUser.value?.email || '',
            role: authRole,
            displayName: currentUser.value?.displayName || undefined,
            activeTenantId,
            tenantRole,
          } as UserProfile
        }
      } else if (authRole === 'tenant' && activeTenantId && tenantRole && tenantMemberData) {
        userProfile.value = {
          uid,
          email: tenantMemberData.email || currentUser.value?.email || '',
          role: authRole,
          displayName: tenantMemberData.displayName || currentUser.value?.displayName || undefined,
          isFirstLogin: tenantMemberData.isFirstLogin === true,
          createdAt: tenantMemberData.createdAt,
          updatedAt: tenantMemberData.updatedAt,
          activeTenantId,
          tenantRole,
        } as UserProfile
      } else {
        // Other roles or no role
        userProfile.value = {
          uid,
          email: currentUser.value?.email || '',
          role: authRole,
          displayName: currentUser.value?.displayName || undefined,
          activeTenantId,
          tenantRole,
        } as UserProfile
      }

    } catch (error) {
      console.error('Error loading user profile:', error)
      if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
      // Even on error, try to set basic profile if we have auth data
      if (currentUser.value) {
        try {
          const idTokenResult = await currentUser.value.getIdTokenResult()
          if (generation !== profileLoadGeneration || currentUser.value?.uid !== uid) return
          const authRole = idTokenResult?.claims?.role as 'admin' | 'affiliate' | 'customer' | 'tenant' | null
          const activeTenantId = typeof idTokenResult?.claims?.activeTenantId === 'string'
            ? idTokenResult.claims.activeTenantId
            : null
          const tenantRole = ['owner', 'admin', 'agent', 'viewer'].includes(String(idTokenResult?.claims?.tenantRole))
            ? idTokenResult?.claims?.tenantRole as 'owner' | 'admin' | 'agent' | 'viewer'
            : null
          userProfile.value = {
            uid,
            email: currentUser.value.email || '',
            role: authRole,
            displayName: currentUser.value.displayName || undefined,
            activeTenantId,
            tenantRole,
          } as UserProfile
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError)
        }
      }
    }
  }

  // Load affiliate profile from Firestore (including subcollections)
  const loadAffiliateProfile = async (uid: string) => {
    try {
      const affiliateDoc = await getDoc(doc(db, 'affiliates', uid))
      if (affiliateDoc.exists()) {
        const affiliateData = affiliateDoc.data()
        
        // Load bank info from subcollection
        let bankInfo: AffiliateBankInfo | null = null
        try {
          const bankDoc = await getDoc(doc(db, 'affiliates', uid, 'bank', 'info'))
          if (bankDoc.exists()) {
            bankInfo = bankDoc.data() as AffiliateBankInfo
          }
        } catch (e) {
          console.warn('Could not load bank info:', e)
        }
        
        // Load current month metrics from subcollection
        let currentMetrics: AffiliateMetrics | null = null
        try {
          const now = new Date()
          const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
          const metricsDoc = await getDoc(doc(db, 'affiliates', uid, 'metrics', currentPeriod))
          if (metricsDoc.exists()) {
            currentMetrics = metricsDoc.data() as AffiliateMetrics
          }
        } catch (e) {
          console.warn('Could not load metrics:', e)
        }
        
        affiliateProfile.value = {
          ...affiliateData,
          bankInfo,
          currentMetrics
        } as AffiliateProfile
      }
    } catch (error) {
      console.error('Error loading affiliate profile:', error)
    }
  }

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      loading.value = true

      const result = await signInWithEmailAndPassword(auth, email, password)

      // Force token refresh to get latest custom claims
      await result.user.getIdToken(true)

      // Update current user
      currentUser.value = result.user

      // Wait for user profile to load
      await loadUserProfile(result.user.uid)
      if (userProfile.value?.role === 'affiliate') {
        await loadAffiliateProfile(result.user.uid)
        await recordFirstAffiliateLogin(result.user.uid)
      }

      // Update lastLoginAt on the user's profile doc (#054 — was always showing "從未登入"
      // because this field was never written). Best-effort, non-blocking — sign-in succeeds
      // even if the timestamp write fails (e.g. brief Firestore unavailability).
      const collectionName = userProfile.value?.role === 'affiliate' ? 'affiliates'
        : (userProfile.value?.role === 'admin' ? 'admins' : null)
      if (collectionName) {
        setDoc(
          doc(db, collectionName, result.user.uid),
          { lastLoginAt: serverTimestamp() },
          { merge: true }
        ).catch(err => console.error('Failed to update lastLoginAt:', err))
      }

      return result.user
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Sign in with an email and password. Phone-to-email lookup was removed because
  // it required anonymous access to private profile collections.
  const signInWithLoginId = async (loginId: string, password: string) => {
    return signIn(loginId.trim().toLowerCase(), password)
  }

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth)
      currentUser.value = null
      userProfile.value = null
      affiliateProfile.value = null
      tenantProfile.value = null
    } catch (error) {
      console.error('Sign out error:', error)
      throw error
    }
  }

  return {
    // State
    currentUser,
    userProfile,
    affiliateProfile,
    tenantProfile,
    loading,
    
    // Computed
    isLoggedIn,
    isAdmin,
    isAffiliate,
    isCustomer,
    isTenantMember,
    canManageTenant,
    canOperateTenant,
    isSales,
    isAccounting,
    isOP,
    isFullAdmin,
    canManageStaticPages,
    adminMenu,
    adminHome,
    canAccessAdmin,
    isApprovedAffiliate,
    
    // Actions
    initAuth,
    signIn,
    signInWithLoginId,
    logout,
    loadUserProfile,
    loadAffiliateProfile
  }
})
